import { orderPoints } from '../../utils/geometry';

/**
 * Pure canvas-based card detector. No external dependencies.
 *
 * Pipeline: grayscale -> blur -> Sobel edges -> threshold -> contour trace -> polygon approx
 */
export function detectCard(imageData) {
  const { width, height } = imageData;
  const frameArea = width * height;

  const gray = toGrayscale(imageData);
  const blurred = boxBlur(gray, width, height, 2);
  const edges = sobelEdges(blurred, width, height);
  const binary = adaptiveThreshold(edges, width, height);
  const dilated = dilate(binary, width, height);
  const contours = findContours(dilated, width, height);

  let bestCorners = null;
  let bestArea = 0;

  for (const contour of contours) {
    const area = polygonArea(contour);
    if (area < frameArea * 0.15) continue;

    const perimeter = polygonPerimeter(contour);
    const approx = approxPolyDP(contour, 0.02 * perimeter);

    if (approx.length === 4 && area > bestArea && isConvex(approx)) {
      bestArea = area;
      bestCorners = approx;
    }
  }

  if (bestCorners) {
    const ordered = orderPoints(bestCorners);
    return { found: true, corners: ordered, areaRatio: bestArea / frameArea };
  }

  return { found: false, corners: null, areaRatio: 0 };
}

// --- Image processing primitives ---

function toGrayscale(imageData) {
  const { data, width, height } = imageData;
  const gray = new Uint8Array(width * height);
  for (let i = 0; i < gray.length; i++) {
    const j = i * 4;
    gray[i] = (data[j] * 77 + data[j + 1] * 150 + data[j + 2] * 29) >> 8;
  }
  return gray;
}

function boxBlur(src, w, h, radius) {
  const dst = new Uint8Array(w * h);

  const temp = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    let sum = 0;
    for (let x = -radius; x <= radius; x++) {
      sum += src[y * w + Math.max(0, Math.min(x, w - 1))];
    }
    temp[y * w] = (sum / (2 * radius + 1)) | 0;
    for (let x = 1; x < w; x++) {
      sum -= src[y * w + Math.max(0, x - radius - 1)];
      sum += src[y * w + Math.min(w - 1, x + radius)];
      temp[y * w + x] = (sum / (2 * radius + 1)) | 0;
    }
  }

  for (let x = 0; x < w; x++) {
    let sum = 0;
    for (let y = -radius; y <= radius; y++) {
      sum += temp[Math.max(0, Math.min(y, h - 1)) * w + x];
    }
    dst[x] = (sum / (2 * radius + 1)) | 0;
    for (let y = 1; y < h; y++) {
      sum -= temp[Math.max(0, y - radius - 1) * w + x];
      sum += temp[Math.min(h - 1, y + radius) * w + x];
      dst[y * w + x] = (sum / (2 * radius + 1)) | 0;
    }
  }

  return dst;
}

function sobelEdges(src, w, h) {
  const edges = new Uint8Array(w * h);

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const tl = src[(y - 1) * w + (x - 1)];
      const t = src[(y - 1) * w + x];
      const tr = src[(y - 1) * w + (x + 1)];
      const l = src[y * w + (x - 1)];
      const r = src[y * w + (x + 1)];
      const bl = src[(y + 1) * w + (x - 1)];
      const b = src[(y + 1) * w + x];
      const br = src[(y + 1) * w + (x + 1)];

      const gx = -tl - 2 * l - bl + tr + 2 * r + br;
      const gy = -tl - 2 * t - tr + bl + 2 * b + br;

      edges[y * w + x] = Math.min(255, Math.sqrt(gx * gx + gy * gy) | 0);
    }
  }

  return edges;
}

function adaptiveThreshold(edges, w, h) {
  let sum = 0;
  for (let i = 0; i < edges.length; i++) sum += edges[i];
  const mean = sum / edges.length;
  const threshold = Math.max(40, mean * 2.5);

  const binary = new Uint8Array(w * h);
  for (let i = 0; i < edges.length; i++) {
    binary[i] = edges[i] > threshold ? 1 : 0;
  }
  return binary;
}

function dilate(src, w, h) {
  const dst = new Uint8Array(w * h);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      if (
        src[y * w + x] ||
        src[(y - 1) * w + x] ||
        src[(y + 1) * w + x] ||
        src[y * w + (x - 1)] ||
        src[y * w + (x + 1)]
      ) {
        dst[y * w + x] = 1;
      }
    }
  }
  return dst;
}

// --- Contour finding (simplified Moore boundary tracing) ---

function findContours(binary, w, h) {
  const visited = new Uint8Array(w * h);
  const contours = [];

  const dx = [1, 1, 0, -1, -1, -1, 0, 1];
  const dy = [0, 1, 1, 1, 0, -1, -1, -1];

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = y * w + x;
      if (!binary[idx] || visited[idx]) continue;
      if (binary[idx - 1]) continue;

      const contour = traceContour(binary, visited, w, h, x, y, dx, dy);
      if (contour.length >= 20) {
        const sampled =
          contour.length > 200
            ? contour.filter((_, i) => i % Math.ceil(contour.length / 200) === 0)
            : contour;
        contours.push(sampled);
      }
    }
  }

  return contours;
}

function traceContour(binary, visited, w, h, startX, startY, dx, dy) {
  const contour = [];
  let x = startX;
  let y = startY;
  let dir = 0;
  const maxSteps = w * h;

  for (let step = 0; step < maxSteps; step++) {
    contour.push({ x, y });
    visited[y * w + x] = 1;

    let found = false;
    const startDir = (dir + 6) % 8;

    for (let i = 0; i < 8; i++) {
      const d = (startDir + i) % 8;
      const nx = x + dx[d];
      const ny = y + dy[d];

      if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;

      if (binary[ny * w + nx]) {
        x = nx;
        y = ny;
        dir = d;
        found = true;
        break;
      }
    }

    if (!found) break;
    if (x === startX && y === startY && contour.length > 2) break;
  }

  return contour;
}

// --- Polygon approximation (Ramer-Douglas-Peucker) ---

function approxPolyDP(points, epsilon) {
  if (points.length < 3) return [...points];

  let maxDist = 0;
  let splitIdx = 0;
  for (let i = 1; i < points.length; i++) {
    const d = distSq(points[0], points[i]);
    if (d > maxDist) {
      maxDist = d;
      splitIdx = i;
    }
  }

  const half1 = points.slice(0, splitIdx + 1);
  const half2 = [...points.slice(splitIdx), points[0]];

  const simplified1 = rdp(half1, epsilon);
  const simplified2 = rdp(half2, epsilon);

  const result = [...simplified1.slice(0, -1), ...simplified2.slice(0, -1)];
  return result;
}

function rdp(points, epsilon) {
  if (points.length <= 2) return [...points];

  const first = points[0];
  const last = points[points.length - 1];

  let maxDist = 0;
  let maxIdx = 0;

  for (let i = 1; i < points.length - 1; i++) {
    const d = pointToLineDist(points[i], first, last);
    if (d > maxDist) {
      maxDist = d;
      maxIdx = i;
    }
  }

  if (maxDist > epsilon) {
    const left = rdp(points.slice(0, maxIdx + 1), epsilon);
    const right = rdp(points.slice(maxIdx), epsilon);
    return [...left.slice(0, -1), ...right];
  }

  return [first, last];
}

function pointToLineDist(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.sqrt(distSq(p, a));

  const num = Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x);
  return num / Math.sqrt(lenSq);
}

function distSq(a, b) {
  return (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
}

// --- Polygon utilities ---

function polygonArea(points) {
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area) / 2;
}

function polygonPerimeter(points) {
  let perimeter = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    perimeter += Math.sqrt(distSq(points[i], points[j]));
  }
  return perimeter;
}

function isConvex(points) {
  const n = points.length;
  if (n < 3) return false;

  let sign = 0;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const c = points[(i + 2) % n];
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (cross !== 0) {
      if (sign === 0) {
        sign = cross > 0 ? 1 : -1;
      } else if ((cross > 0 ? 1 : -1) !== sign) {
        return false;
      }
    }
  }
  return true;
}
